/**
 * Saved contact views (stored in the `saved_views` user setting as JSON) and
 * the filter spec that "enroll from a saved view" resolves to contact ids.
 */

import { containsFilter } from './customFields'
import type { CustomFieldDef } from './types'

export interface CustomFieldFilter {
  key: string
  value: string
}

/** Subset of the contacts list filters that can be saved and replayed. */
export interface ViewFilters {
  search: string
  stage: string
  country: string
  location: string
  phonePrefix: string
  scoreMin: string
  scoreMax: string
  tagFilter: string
  customField: CustomFieldFilter | null
}

export interface SavedView extends Omit<ViewFilters, 'customField'> {
  name: string
  customField?: CustomFieldFilter
}

export const EMPTY_VIEW_FILTERS: ViewFilters = {
  search: '',
  stage: '',
  country: '',
  location: '',
  phonePrefix: '',
  scoreMin: '',
  scoreMax: '',
  tagFilter: '',
  customField: null,
}

function s(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

export function parseSavedViews(raw: string): SavedView[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const out: SavedView[] = []
    for (const v of parsed) {
      if (!v || typeof v !== 'object') continue
      const o = v as Record<string, unknown>
      const name = s(o.name).trim()
      if (!name) continue
      const cf = o.customField as Record<string, unknown> | undefined
      const view: SavedView = {
        name,
        search: s(o.search),
        stage: s(o.stage),
        country: s(o.country),
        location: s(o.location),
        phonePrefix: s(o.phonePrefix),
        scoreMin: s(o.scoreMin),
        scoreMax: s(o.scoreMax),
        tagFilter: s(o.tagFilter),
      }
      if (cf && s(cf.key) && s(cf.value)) view.customField = { key: s(cf.key), value: s(cf.value) }
      out.push(view)
    }
    return out
  } catch {
    return []
  }
}

export function viewToFilters(view: SavedView): ViewFilters {
  return { ...view, customField: view.customField ?? null }
}

/** Stage values that are list shortcuts rather than real stages. */
export const SPECIAL_STAGES = new Set(['__nodomain', '__noemail', '__stale'])

export interface ViewSummaryPart {
  label: string
  value: string
}

/** Short chips describing a view, for the enroll dialog. */
export function describeFilters(
  f: ViewFilters,
  defs: readonly Pick<CustomFieldDef, 'key' | 'label'>[] = [],
): ViewSummaryPart[] {
  const parts: ViewSummaryPart[] = []
  if (f.search) parts.push({ label: 'Search', value: f.search })
  if (f.stage) parts.push({ label: 'Stage', value: f.stage })
  if (f.country) parts.push({ label: 'Country', value: f.country })
  if (f.location) parts.push({ label: 'Location', value: f.location })
  if (f.phonePrefix) parts.push({ label: 'Phone', value: f.phonePrefix })
  if (f.scoreMin || f.scoreMax)
    parts.push({ label: 'Score', value: `${f.scoreMin || '0'} to ${f.scoreMax || '100'}` })
  if (f.tagFilter) parts.push({ label: 'Tag', value: f.tagFilter })
  if (f.customField) {
    const def = defs.find((d) => d.key === f.customField?.key)
    parts.push({ label: def?.label ?? f.customField.key, value: f.customField.value })
  }
  return parts
}

/** True when the view has no condition at all (it would enrol every contact). */
export function isUnfiltered(f: ViewFilters): boolean {
  return describeFilters(f).length === 0
}

/** The jsonb containment object for the custom-field part of a filter, or null. */
export function customFieldContains(
  f: ViewFilters,
  defs: readonly CustomFieldDef[],
): Record<string, unknown> | null {
  if (!f.customField) return null
  const def = defs.find((d) => d.key === f.customField?.key && d.entity === 'contact')
  if (!def) return null
  return containsFilter(def, f.customField.value)
}
